/*============================================================================
  HC - IB cart drawer v2 controller - 2026-08-20

  Drives snippets/ib-cart-drawer.liquid. Loaded from layout/theme.liquid only
  when settings.cart_drawer_v2 is on, immediately after theme.js and BEFORE
  ib-cart-notice.js — see the cart:add:error note below, the position is a
  contract, not a convenience.

  TWO SURFACES, ONE CONTROLLER (2026-08-21). snippets/ib-cart-page.liquid is the
  /cart build of the same design, and it boots this same file off
  [data-ib-cart-page] under settings.cart_page_v2. The two roots are mutually
  exclusive by URL — the drawer portal in layout/theme.liquid is gated `unless
  request.page_type == 'cart'` — so exactly one of them exists per page and the
  single-root lookup in boot() stays valid. The page instance is still assigned
  to window.IBCartDrawer, which is the global ib-cart-nudges.js and
  ib-cart-save.js reach the modal helpers through.

  What the page surface skips: open/close, the scrim, the header trigger and its
  prefetch warm, the scroll lock, sizeDrawer, the MobileNav/modalOpen close
  hooks and the CartDrawer:open/close emits — a page is not a sheet. `isOpen` is
  permanently true there, which is what makes save's "open the drawer before
  replaying" branch no-op on its own.

  COMPANION MODE is the one real asymmetry. theme.js initCartPage() constructs a
  theme.CartForm against #CartPageForm at DOMready and owns every fragment
  render on that surface; a second CartForm on the same form would double-bind
  the quantity writes. So on the page this controller constructs none, renders
  no fragments, and does shell work only — count line, empty state, the
  remove/clear confirms (which is how /cart gets AJAX remove for the first
  time), the note toggle and the checkout-spinner net. The two CartForm methods
  it still needs are borrowed off the prototype rather than copied; see
  showError() and updateBubble().

  What this file is NOT: a cart transport. Every mutation goes through the
  theme.cart API (queue, challenge suppression, event bus) and every quantity
  write, optimistic paint and re-render goes through theme.CartForm. This
  controller only owns open/close, the confirm modals, the count line and the
  event wiring that stock theme.HeaderCart used to own.

  theme.HeaderCart is inert while v2 is on: sections/header.liquid stops
  rendering #HeaderCart, and the HeaderCart constructor bails when that element
  is missing. Nothing in assets/theme.js is patched at the prototype level; the
  single instance-level patch is cartForm.isDrawerHidden (see boot()).

  Load-time boot is deliberate. As a deferred script we run after the document
  has parsed (so the drawer markup exists) but before DOMContentLoaded, which is
  what puts our cart:add:error listener ahead of ib-cart-notice.js's.
==============================================================================*/
(function() {
  'use strict';

  var SELECTORS = {
    root: '[data-ib-cart-drawer]',
    pageRoot: '[data-ib-cart-page]',
    seededCart: '[data-ib-cart-state]',
    scrim: '[data-ib-cart-scrim]',
    trigger: '#HeaderCartTrigger',
    closeBtn: '[data-ib-cart-close]',
    form: 'form.cart__drawer-form',
    pageForm: '#CartPageForm',
    count: '[data-ib-cart-count]',
    note: '[data-ib-cart-note]',
    noteToggle: '[data-ib-note-toggle]',
    noteInput: '[name="note"]',
    clearBtn: '[data-ib-cart-clear]',
    removeBtn: '.hc-cart-ajax--remove',
    qtyInput: '.js-qty__num',
    checkoutBtn: '[name="checkout"]',
    lineItem: '.cart__item',
    pageTitle: '.ib-cart-page__title',
    modal: '[data-ib-cart-modal]',
    modalCancel: '[data-ib-cart-modal-cancel]',
    removeConfirm: '[data-ib-remove-confirm]',
    clearConfirm: '[data-ib-clear-confirm]',
    focusable: 'a[href], button:not([disabled]), textarea, input, select'
  };

  var CART_AJAX_SECTION = 'cart-drawer-ajax';
  var NAMESPACE = '.ib-cart-drawer';

  /*
    Dialogs other modules mount over these surfaces.
    assets/ib-ordering-under-prompt.js appends its host to <body> and removes it
    again on close, so merely existing means open; assets/ib-gift-choice-ui.js
    keeps one root in the document for the life of the page and toggles `hidden`
    on it. See externalDialogOpen() for why this file has to know about them.
  */
  var OU_DIALOG = '[data-ib-ou-dialog]';
  var GIFT_MODAL_ROOT_ID = 'ib-gift-choice-modal-root';
  var EDUCATION_DIALOG = '[data-ib-education-modal]:not([hidden])';

  // Ceiling on flushPendingEdits(): theme-cart.js's 300ms line-write debounce
  // plus room for the request. Past it the caller reads whatever the cart layer
  // has, which is still never worse than not having flushed at all.
  var FLUSH_SETTLE_CEILING_MS = 4000;

  function hasTheme() {
    return typeof theme !== 'undefined' && theme && theme.cart;
  }

  function track(event, payload) {
    var T = window.IBCartTelemetry;
    if (T && typeof T.track === 'function') {
      T.track(event, payload);
    }
  }

  function emit(type) {
    if (!document.dispatchEvent || typeof CustomEvent !== 'function') {
      return;
    }
    document.dispatchEvent(new CustomEvent(type));
  }

  function isChallengeActive() {
    return Boolean(hasTheme() && theme.cart.isChallengeActive && theme.cart.isChallengeActive());
  }

  function money(cents) {
    var value = parseInt(cents, 10);
    if (isNaN(value)) {
      return '';
    }
    // Deliberately NOT theme.Currency.formatMoney: this store's money format
    // carries HTML ($65<sup>80</sup>), which is unsafe for textContent writes
    // and wrong for the plain-money style these surfaces use (per the mockup).
    var dollars = Math.floor(Math.abs(value) / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var centsPart = String(Math.abs(value) % 100);
    if (centsPart.length < 2) {
      centsPart = '0' + centsPart;
    }
    return (value < 0 ? '-' : '') + '$' + dollars + '.' + centsPart;
  }

  /*
    HC - locale strings (PR E) - 2026-08-20
    layout/theme.liquid bridges the cart.general.* keys into theme.strings with
    [token] placeholders, the same convention the stock cartFreeShippingProgress
    string uses. The English fallback stays here on purpose: the bridge is gated
    on settings.cart_drawer_v2, so a stale cached layout must degrade to English
    rather than to "undefined".
  */
  // Shopify's `t` filter HTML-escapes every non-_html translation, so bridged
  // strings arrive entity-encoded ("You&#39;ve ..."). Decode ONCE here; without
  // this, render-time escaping doubled the entities and they showed literally.
  // Shopify's escaper only ever emits these five entities, so a pure string
  // decode covers the bridge without touching the DOM.
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) {
      return value;
    }
    return value
      .replace(/&#0*39;|&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  }

  function themeString(key, fallback, tokens) {
    var strings = typeof theme !== 'undefined' && theme.strings ? theme.strings : null;
    var value = strings && typeof strings[key] === 'string' && strings[key] ? decodeEntities(strings[key]) : fallback;
    if (!tokens) {
      return value;
    }
    for (var token in tokens) {
      if (Object.prototype.hasOwnProperty.call(tokens, token)) {
        value = value.split('[' + token + ']').join(tokens[token]);
      }
    }
    return value;
  }

  function itemsLabel(count) {
    return count === 1
      ? themeString('cartItemsCountOne', '[count] item', { count: count })
      : themeString('cartItemsCountMany', '[count] items', { count: count });
  }

  function unitsLabel(count) {
    return count === 1
      ? themeString('cartUnitsCountOne', '[count] unit', { count: count })
      : themeString('cartUnitsCountMany', '[count] units', { count: count });
  }

  function isEscape(evt) {
    return evt && (evt.key === 'Escape' || evt.key === 'Esc' || evt.keyCode === 27);
  }

  function isTab(evt) {
    return evt && (evt.key === 'Tab' || evt.keyCode === 9);
  }

  function setHidden(el, hidden) {
    if (!el) {
      return;
    }
    if (hidden) {
      el.hidden = true;
      el.setAttribute('hidden', 'hidden');
    } else {
      el.hidden = false;
      el.removeAttribute('hidden');
    }
  }

  function textInto(el, value) {
    if (el) {
      el.textContent = value;
    }
  }

  // First-paint cart JSON, stamped by snippets/ib-cart-page.liquid. The drawer
  // gets authoritative line facts from a warm fetch on open; a page has no
  // "open", so it is handed the same facts inline instead of buying them with a
  // request. Absent (drawer surface, or a stale cached page) is fine — every
  // caller falls back to reading the rendered rows.
  function readSeededCart(root) {
    var node = root.querySelector(SELECTORS.seededCart);
    if (!node) {
      return null;
    }
    try {
      var parsed = JSON.parse(node.textContent || 'null');
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function IBCartDrawer(root, surface) {
    this.root = root;
    this.surface = surface === 'page' ? 'page' : 'drawer';
    this.isPage = this.surface === 'page';

    // theme.CartForm namespaces its events off the form's data-location and
    // stamps the same value on every cart:rendered it dispatches. This is the
    // value that means "our own surface rendered it".
    this.renderedLocation = this.isPage ? 'page' : 'header';

    this.scrim = this.isPage ? null : document.querySelector(SELECTORS.scrim);
    this.trigger = this.isPage ? null : document.querySelector(SELECTORS.trigger);
    this.form = root.querySelector(this.isPage ? SELECTORS.pageForm : SELECTORS.form);
    this.countEl = root.querySelector(SELECTORS.count);
    this.closeBtn = this.isPage ? null : root.querySelector(SELECTORS.closeBtn);
    this.note = root.querySelector(SELECTORS.note);
    this.noteToggle = root.querySelector(SELECTORS.noteToggle);

    // theme.CartForm calls the form's parent its `wrapper` and hangs `is-empty`
    // and the .cart__errors block off it. Companion mode borrows two of that
    // prototype's methods, so the controller answers to the same property name
    // rather than inventing a second vocabulary for the same node.
    this.wrapper = this.form && this.form.parentNode ? this.form.parentNode : root;

    // The page IS the cart: it is never closed. Saying so here is what makes
    // every isOpen-gated branch read correctly without a page special case —
    // notably ib-cart-save.js's replay(), which opens the drawer only when it
    // finds one closed.
    this.isOpen = this.isPage;
    this.noteDirty = false;
    this.cartDrawerStale = true;
    this.pendingRemove = null;
    this.lastModalTrigger = null;
    this.lastFocus = null;
    this.seededCart = readSeededCart(root);

    this.boot();
  }

  IBCartDrawer.prototype = {

    boot: function() {
      // Point the whole Section Rendering pipeline (add/update responses,
      // getEmbeddedCartState) at our own fragment. theme-cart.js reads this
      // property at call time, so flipping it here is enough. Both v2 surfaces
      // want it — the page's CartForm consumes exactly the same fragment, since
      // _parseProductHTML only ever asks for .cart__items / .cart__discounts —
      // and it is guarded by boot() having found a root, so a v1 storefront
      // never reaches this line.
      theme.cart.cartAjaxSection = CART_AJAX_SECTION;

      if (this.isPage) {
        this.bootPage();
      } else {
        this.bootDrawer();
      }

      this.bindKeys();
      this.bindTools();
      this.bindModals();
      this.bindCartEvents();
    },

    bootDrawer: function() {
      this.cartForm = new theme.CartForm(this.form);

      // Stock isDrawerHidden() sniffs form.closest('#HeaderCart'), which no
      // longer exists — it would report "visible" for every hidden render and
      // re-run runInteractiveInit (and with it a document-wide
      // StorefrontExpressButtons rebuild) on every add. Patch the instance.
      var self = this;
      this.cartForm.isDrawerHidden = function() {
        return !self.isOpen;
      };

      if (typeof theme.QuickAdd === 'function') {
        this.quickAdd = new theme.QuickAdd(this.root);
      }
      if (typeof theme.QuickShop === 'function') {
        this.quickShop = new theme.QuickShop(this.root);
      }

      this.bindOpenClose();
      this.bindPrefetch();
    },

    /*
      Companion mode. NO CartForm and NO QuickAdd/QuickShop here: theme.js
      initCartPage() constructs both against this page at DOMready (the form by
      id, quick-add against .cart-recommendations[data-location="page"]), and a
      second binding of either would fire every quantity write and every
      quick-add twice.
    */
    bootPage: function() {
      var self = this;

      // theme.CartForm.onSubmit stamps .btn--loading on the checkout button and
      // only the terms-unchecked branch ever takes it off. The drawer clears it
      // on every open; a page has no open, so it clears on the two moments that
      // mean "the shopper is looking at this again": a bfcache restore, and any
      // settled cart update after an interrupted submit (the ordering-under
      // gate is the one that interrupts).
      if (window && typeof window.addEventListener === 'function') {
        window.addEventListener('pageshow', function() {
          self.clearCheckoutSpinner();
        });
      }

      // A note typed and then abandoned by clicking straight through to
      // checkout would be lost inside CartForm's own 500ms debounce. The drawer
      // flushes on close; blur is this surface's equivalent, and it lands
      // before the checkout button's click.
      var noteInput = this.root.querySelector(SELECTORS.noteInput);
      if (noteInput && noteInput.addEventListener) {
        noteInput.addEventListener('blur', function() {
          self.flushNote();
        });
      }
    },

    /*========================================================================
      Binding
    ========================================================================*/

    bindOpenClose: function() {
      var self = this;

      if (this.trigger && this.trigger.addEventListener) {
        // Stock HeaderCart no longer binds this — its DOM is gone. The trigger
        // is an <a href="/cart">, so the handler must preventDefault.
        this.trigger.addEventListener('click', function(evt) {
          self.open(evt);
        });
      }

      if (this.scrim && this.scrim.addEventListener) {
        this.scrim.addEventListener('click', function() {
          self.close();
        });
      }

      if (this.closeBtn && this.closeBtn.addEventListener) {
        this.closeBtn.addEventListener('click', function() {
          self.close();
        });
      }

      // Same close-on-conflict wiring stock HeaderCart had.
      document.addEventListener('MobileNav:open', function(evt) {
        self.close(evt);
      });
      document.addEventListener('modalOpen', function(evt) {
        self.close(evt);
      });
    },

    /*
      Keys are bound on BOTH surfaces: the confirm/offers/save dialogs exist on
      the page too, and they need the same single ordered handler. The sheet
      half of each branch is a no-op on the page — close() returns early there,
      and trapTab() refuses to trap a document.
    */
    bindKeys: function() {
      var self = this;

      // One Escape handler, ordered: an open confirm modal consumes the key and
      // stops it, so Escape never closes the drawer out from under a modal.
      // Tab shares the handler rather than adding a second listener, so the
      // same "modal outranks the sheet" ordering governs both keys.
      document.addEventListener('keydown', function(evt) {
        // Stand down entirely while another module's dialog is up. Both keys,
        // not just Tab: whatever is on top owns Escape too, and closing the
        // sheet out from under it would take the shopper's place with it.
        if (self.externalDialogOpen()) {
          return;
        }
        if (isTab(evt)) {
          self.trapTab(evt);
          return;
        }
        if (!isEscape(evt)) {
          return;
        }
        var openModal = self.getOpenModal();
        if (openModal) {
          if (evt.stopPropagation) {
            evt.stopPropagation();
          }
          self.closeModal(openModal);
          return;
        }
        if (self.isOpen) {
          self.close();
        }
      });
    },

    bindTools: function() {
      var self = this;

      if (this.noteToggle && this.noteToggle.addEventListener) {
        this.noteToggle.addEventListener('click', function() {
          self.toggleNote();
        });
      }

      // CartForm already binds a debounced theme.cart.updateNote to this input;
      // we only track dirtiness so close() can flush inside the debounce window.
      var noteInput = this.root.querySelector(SELECTORS.noteInput);
      if (noteInput && noteInput.addEventListener) {
        noteInput.addEventListener('input', function() {
          self.noteDirty = true;
          self.autogrowNote(noteInput);
        });
      }

      if (this.root.addEventListener) {
        this.root.addEventListener('click', function(evt) {
          var target = evt.target;
          if (!target || !target.closest) {
            return;
          }

          var remove = target.closest(SELECTORS.removeBtn);
          if (remove) {
            evt.preventDefault();
            self.askRemove(remove);
            return;
          }

          var clear = target.closest(SELECTORS.clearBtn);
          if (clear) {
            evt.preventDefault();
            self.askClear(clear);
          }
        });
      }
    },

    bindModals: function() {
      var self = this;
      var modals = document.querySelectorAll(SELECTORS.modal);

      Array.prototype.forEach.call(modals, function(modal) {
        modal.addEventListener('click', function(evt) {
          // Only a click on the scrim itself dismisses.
          if (evt.target === modal) {
            self.closeModal(modal);
          }
        });

        var cancel = modal.querySelector(SELECTORS.modalCancel);
        if (cancel) {
          cancel.addEventListener('click', function() {
            self.closeModal(modal);
          });
        }

        var removeConfirm = modal.querySelector(SELECTORS.removeConfirm);
        if (removeConfirm) {
          removeConfirm.addEventListener('click', function() {
            self.confirmRemove();
          });
        }

        var clearConfirm = modal.querySelector(SELECTORS.clearConfirm);
        if (clearConfirm) {
          clearConfirm.addEventListener('click', function() {
            self.confirmClear();
          });
        }
      });
    },

    bindCartEvents: function() {
      var self = this;

      // Transcribed from theme.HeaderCart.init (assets/theme.js ~6349-6376).
      // The branching is load-bearing: renderedLocation/countOnly/optimistic
      // decide whether we render now, defer a rebuild, or do nothing at all.
      document.addEventListener('cart:updated', function(evt) {
        var detail = evt && evt.detail ? evt.detail : {};
        var cartState = theme.cart.getCartState(detail);
        var renderPayload = theme.cart.getRenderPayload(detail);

        if (cartState) {
          self.seededCart = cartState;
        }

        if (cartState || typeof detail.item_count === 'number') {
          self.updateLightweightCartState(detail);
        }

        // Second half of the page's spinner net (the first is `pageshow`): any
        // cart mutation is proof the shopper is still on the page rather than
        // mid-navigation, so an interrupted submit stops looking busy. The
        // drawer does this on open instead.
        if (self.isPage) {
          self.clearCheckoutSpinner();
        }

        // Companion mode renders nothing: theme.js initCartPage() holds the
        // page's CartForm and already handles every payload whose
        // renderedLocation is not 'page' (assets/theme.js ~10522). Rendering
        // here as well would swap [data-products] twice per mutation and re-run
        // the document-wide express-button rebuild with it.
        if (!self.isPage && detail.renderedLocation !== 'header' && !detail.countOnly) {
          if (renderPayload) {
            if (self.cartForm.renderCartPayload(renderPayload, { source: detail.source || 'cart-updated' })) {
              self.cartDrawerStale = false;
            } else if (self.isOpen) {
              self.cartDrawerStale = false;
              self.cartForm.buildCart();
            } else {
              self.cartDrawerStale = true;
            }
          } else if (cartState && !detail.optimistic) {
            if (self.isOpen) {
              self.cartDrawerStale = false;
              self.cartForm.buildCart();
            } else {
              self.cartDrawerStale = true;
            }
          }
        }

        if (detail.openCart && !self.isOpen) {
          self.open();
        }
      });

      // CartForm dispatches this after every successful render, from any path.
      document.addEventListener('cart:rendered', function(evt) {
        var detail = evt && evt.detail ? evt.detail : {};
        if (detail.location && detail.location !== self.renderedLocation) {
          return;
        }
        var rendered = detail.cartState || detail.cart || null;
        if (rendered) {
          self.seededCart = rendered;
        }
        // theme.CartForm.cartMarkup() already toggles is-empty from the
        // section's data-count, but only when it has markup to parse. Re-apply
        // it from the authoritative state so a render that arrives without one
        // (or ahead of the count-only update) still lands the empty state.
        if (rendered && typeof rendered.item_count === 'number') {
          self.applyEmptyState(rendered.item_count);
        }
        self.renderCountLine(rendered);
      });

      // Surface add failures inside the drawer when it is the surface the
      // shopper is looking at, and CLAIM the error so exactly one surface
      // renders it. This listener is registered at parse time, ahead of
      // ib-cart-notice.js's, so renderedInline is already true when the notice
      // script decides whether to toast — which is what resolves the v1
      // double-render (stock HeaderCart bound this inside DOMready, i.e. after
      // the toast had already claimed the event).
      document.addEventListener('cart:add:error', function(evt) {
        var detail = evt && evt.detail && typeof evt.detail === 'object' ? evt.detail : {};
        if (self.isOpen) {
          self.showError(detail.message || themeString('cartErrorAdd', 'Unable to add item to cart. Please try again.'));
          detail.renderedInline = true;
        }
      });

      document.addEventListener('cart:open', function(evt) {
        if (!self.isOpen) {
          self.open(evt);
        }
      });

      document.addEventListener('cart:close', function(evt) {
        self.close(evt);
      });
    },

    bindPrefetch: function() {
      if (!this.trigger || !this.trigger.addEventListener) {
        return;
      }

      // Warm a stale drawer before the click lands so open() never waits on a
      // fetch. warmDrawer() self-guards on the challenge flag.
      var warm = this.warmDrawer.bind(this);
      ['pointerenter', 'focusin', 'touchstart'].forEach(function(eventName) {
        this.trigger.addEventListener(eventName, warm, { passive: true });
      }.bind(this));
    },

    /*========================================================================
      Open / close
    ========================================================================*/

    open: function(evt) {
      // A page cannot be opened. Guarding on the surface rather than on isOpen
      // is deliberate: `cart:open` and `detail.openCart` fire site-wide, and on
      // /cart the right answer is "you are already looking at it".
      if (this.isPage) {
        return;
      }
      if (evt && evt.preventDefault) {
        evt.preventDefault();
      }
      if (this.isOpen) {
        return;
      }

      // An optimistic open runs BEFORE the add resolves: the response's bundled
      // section renders the drawer, so warming here would be a wasted request
      // (and a forbidden one against a challenged session).
      var optimistic = Boolean(evt && evt.detail && evt.detail.optimistic);
      if (optimistic) {
        track('drawer:optimistic-open');
      } else {
        this.warmDrawer();
      }

      if (this.cartForm && typeof this.cartForm.forceSyncQuantityInputs === 'function') {
        this.cartForm.forceSyncQuantityInputs();
      }

      // HC - checkout spinner recovery net - 2026-08-21
      // A submit that is interrupted (the ordering-under gate) or that simply
      // never navigates would leave the button spinning for the rest of the
      // session, so every open starts it from rest.
      this.clearCheckoutSpinner();

      this.sizeDrawer();

      this.lastFocus = document.activeElement || null;

      setHidden(this.scrim, false);
      setHidden(this.root, false);
      // .site-header__cart + is-active is what sections/promotional-display.liquid
      // watches to hide its sticky header.
      this.root.classList.add('is-active');
      document.documentElement.classList.add('cart-open');

      if (this.root.scrollTop !== undefined) {
        this.root.scrollTop = 0;
      }

      this.lockScrolling();

      this.isOpen = true;
      this.setTriggerExpanded(true);

      emit('CartDrawer:open');
      emit('drawerOpen');

      this.focusFirst();

      // Renders that happened while hidden deferred their stepper bindings.
      if (this.cartForm && typeof this.cartForm.flushDeferredInit === 'function') {
        this.cartForm.flushDeferredInit();
      }
    },

    close: function(evt) {
      if (this.isPage || !this.isOpen) {
        return;
      }

      this.flushDirtyQuantityInputs();
      this.flushNote();

      var openModal = this.getOpenModal();
      if (openModal) {
        this.closeModal(openModal, true);
      }

      setHidden(this.root, true);
      setHidden(this.scrim, true);
      this.root.classList.remove('is-active');
      document.documentElement.classList.remove('cart-open');

      this.unlockScrolling();

      this.isOpen = false;
      this.setTriggerExpanded(false);

      emit('CartDrawer:close');
      emit('drawerClose');

      this.restoreFocus(this.lastFocus);
      this.lastFocus = null;
    },

    // Stock only locks when a sticky header is present; v2 is a full-height
    // sheet with its own scroll region, so the page behind it always locks.
    lockScrolling: function() {
      if (theme.a11y && typeof theme.a11y.lockMobileScrolling === 'function') {
        theme.a11y.lockMobileScrolling(NAMESPACE);
      }
    },

    unlockScrolling: function() {
      if (theme.a11y && typeof theme.a11y.unlockMobileScrolling === 'function') {
        theme.a11y.unlockMobileScrolling(NAMESPACE);
      }
    },

    clearCheckoutSpinner: function() {
      var checkoutBtn = this.root ? this.root.querySelector(SELECTORS.checkoutBtn) : null;
      if (checkoutBtn && checkoutBtn.classList) {
        checkoutBtn.classList.remove('btn--loading');
      }
    },

    // theme.sizeDrawer reads #HeaderWrapper.offsetHeight and throws without it.
    sizeDrawer: function() {
      if (typeof theme.sizeDrawer !== 'function') {
        return;
      }
      try {
        theme.sizeDrawer();
      } catch (error) {
        /* the sheet is 100dvh regardless; --maxDrawerHeight is a nicety */
      }
    },

    focusFirst: function() {
      var target = this.closeBtn || this.root.querySelector(SELECTORS.focusable);
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    },

    restoreFocus: function(el) {
      if (el && typeof el.focus === 'function' && !el.disabled) {
        el.focus();
      }
    },

    // snippets/header-icons.liquid renders aria-expanded only under the v2
    // setting, and this file loads only under the same setting — so whenever
    // the attribute exists, this is what keeps it honest.
    setTriggerExpanded: function(expanded) {
      if (this.trigger && this.trigger.setAttribute) {
        this.trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    },

    /*========================================================================
      Focus trap

      The sheet is aria-modal="true", which tells a screen reader the rest of
      the page is inert but does nothing to Tab. Without this, Tab walks
      straight out of the drawer into the page behind it — which is still fully
      focusable, because v2 hides nothing outside the sheet.
    ========================================================================*/

    /*
      Three modules mount full-screen dialogs OVER these surfaces: the
      ordering-under gate, the gift chooser, and the Education cart guard. They run their own focus trap,
      and ours cannot see inside either of them — getOpenModal() only knows this
      file's [data-ib-cart-modal] dialogs, so focus sitting in one of theirs
      reads as "outside the drawer" and trapTab yanks it back on EVERY Tab.
      That is what made keyboard checkout through the ordering-under gate
      impossible. While one of them is up we do nothing at all and let its trap
      work; both surfaces answer the same way, because both render both dialogs.
    */
    externalDialogOpen: function() {
      if (typeof document.querySelector === 'function' && document.querySelector(EDUCATION_DIALOG)) {
        return true;
      }
      if (typeof document.querySelector === 'function' && document.querySelector(OU_DIALOG)) {
        return true;
      }
      var giftModal = typeof document.getElementById === 'function'
        ? document.getElementById(GIFT_MODAL_ROOT_ID)
        : null;
      return Boolean(giftModal && !giftModal.hidden);
    },

    trapTab: function(evt) {
      if (this.externalDialogOpen()) {
        return;
      }

      // A modal outranks the sheet: it renders above it and its own Escape
      // branch already treats it as the active surface.
      //
      // On the page surface a modal is the ONLY thing worth trapping. The page
      // root is not a dialog — it has a header and a footer either side of it —
      // so trapping Tab inside it would strand keyboard users in the cart.
      var container = this.getOpenModal();
      if (!container && !this.isPage && this.isOpen) {
        container = this.root;
      }
      if (!container) {
        return;
      }

      var focusables = this.focusablesIn(container);
      if (!focusables.length) {
        return;
      }

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement || null;
      var backwards = Boolean(evt.shiftKey);
      var at = focusables.indexOf(active);

      // Focus outside the container — a cart render replaced the focused node,
      // or the shopper reached the drawer from the page — restarts at the edge
      // Tab was heading for.
      if (at === -1) {
        this.wrapFocus(evt, backwards ? last : first);
        return;
      }
      if (!backwards && active === last) {
        this.wrapFocus(evt, first);
        return;
      }
      if (backwards && active === first) {
        this.wrapFocus(evt, last);
      }
    },

    wrapFocus: function(evt, target) {
      if (!target || typeof target.focus !== 'function') {
        return;
      }
      if (evt && evt.preventDefault) {
        evt.preventDefault();
      }
      target.focus();
    },

    focusablesIn: function(container) {
      var nodes = container.querySelectorAll(SELECTORS.focusable);
      var out = [];

      for (var i = 0; i < nodes.length; i += 1) {
        var node = nodes[i];
        if (node.disabled) {
          continue;
        }
        if (node.getAttribute && node.getAttribute('tabindex') === '-1') {
          continue;
        }
        if (this.isHiddenForFocus(node, container)) {
          continue;
        }
        out.push(node);
      }

      return out;
    },

    // Two checks because two things hide controls here. `hidden` is walked up
    // the ancestor chain: the note block, the save dialog's inactive step and
    // the recommendations all hide a WRAPPER, not the controls inside it.
    // offsetParent catches what is hidden by CSS alone (the tools row under
    // .is-empty) and is simply absent from the test DOM, which has no layout.
    isHiddenForFocus: function(node, container) {
      var el = node;
      while (el && el !== container.parentNode) {
        if (el.hidden) {
          return true;
        }
        el = el.parentNode;
      }
      return 'offsetParent' in node && node.offsetParent === null;
    },

    /*========================================================================
      Rendering
    ========================================================================*/

    // Port of theme.HeaderCart.updateLightweightCartState, plus the v2 count
    // line. Runs on count-only/optimistic updates where no markup is rendered.
    updateLightweightCartState: function(detail) {
      var count = null;
      var cartTotals = detail.cartState || detail.cart || null;

      if (cartTotals && typeof cartTotals.item_count === 'number') {
        count = cartTotals.item_count;
      } else if (typeof detail.item_count === 'number') {
        count = detail.item_count;
      }

      if (count === null) {
        return;
      }

      this.updateBubble(count);
      this.applyEmptyState(count);

      // Only a final cart total may move the shipping progress; optimistic and
      // count-only updates are skipped, exactly as stock did. The indicator is
      // a CartForm-owned node, so this half is drawer-only.
      if (this.cartForm && cartTotals && typeof cartTotals.total_price === 'number') {
        this.cartForm.updateFreeShippingIndicator(cartTotals.total_price, count);
      }

      this.renderCountLine(cartTotals, count);
    },

    /*
      The two theme.CartForm methods companion mode still needs. Both are
      BORROWED off the prototype rather than copied, so the page keeps one
      implementation of the header bubble and one of the inline error block
      instead of a second copy free to drift from the drawer's.

      updateCount touches nothing on `this` (it writes .cart-link__bubble-num
      and .cart-link__bubble document-wide); showError reads `this.wrapper`,
      which the constructor sets to the form's parent for exactly this reason.
    */
    cartFormProto: function() {
      return theme.CartForm && theme.CartForm.prototype ? theme.CartForm.prototype : null;
    },

    updateBubble: function(count) {
      if (this.cartForm && typeof this.cartForm.updateCount === 'function') {
        this.cartForm.updateCount(count);
        return;
      }
      var proto = this.cartFormProto();
      if (proto && typeof proto.updateCount === 'function') {
        proto.updateCount.call(this, count);
      }
    },

    showError: function(message) {
      if (this.cartForm && typeof this.cartForm.showError === 'function') {
        this.cartForm.showError(message);
        return;
      }
      var proto = this.cartFormProto();
      if (proto && typeof proto.showError === 'function') {
        proto.showError.call(this, message);
        return;
      }
      console.warn(message);
    },

    applyEmptyState: function(count) {
      if (count > 0) {
        this.root.classList.remove('is-empty');
      } else {
        this.root.classList.add('is-empty');
      }
    },

    // "N items · M units" — items is the LINE count, units is cart.item_count
    // (Shopify's item_count is the sum of quantities, not the number of lines).
    renderCountLine: function(cartState, fallbackUnits) {
      if (!this.countEl) {
        return;
      }

      var units = cartState && typeof cartState.item_count === 'number'
        ? cartState.item_count
        : (typeof fallbackUnits === 'number' ? fallbackUnits : this.countRenderedUnits());

      var lines = cartState && cartState.items && typeof cartState.items.length === 'number'
        ? cartState.items.length
        : this.countRenderedLines();

      if (typeof units !== 'number' || isNaN(units)) {
        return;
      }

      this.countEl.textContent = itemsLabel(lines) + ' · ' + unitsLabel(units);
    },

    countRenderedLines: function() {
      var rows = this.root.querySelectorAll(SELECTORS.lineItem);
      return rows ? rows.length : 0;
    },

    countRenderedUnits: function() {
      var inputs = this.root.querySelectorAll(SELECTORS.qtyInput);
      var total = 0;
      for (var i = 0; i < inputs.length; i += 1) {
        var qty = parseInt(inputs[i].value, 10);
        if (!isNaN(qty)) {
          total += qty;
        }
      }
      return total;
    },

    // Port of theme.HeaderCart.warmDrawer. A failed warm keeps the stale flag
    // so the next signal retries.
    warmDrawer: function() {
      if (!this.cartDrawerStale || this._drawerWarming) {
        return;
      }

      if (isChallengeActive()) {
        return;
      }

      this._drawerWarming = true;

      var self = this;
      Promise.resolve(this.cartForm.buildCart())
        .then(function(rebuilt) {
          if (rebuilt !== false) {
            self.cartDrawerStale = false;
          } else if (self.isOpen) {
            self.cartForm.showError(themeString('cartErrorRefresh', 'Unable to refresh cart. Please try again.'));
          }
        })
        .catch(function(error) {
          console.warn('Cart drawer refresh failed:', error);
        })
        .then(function() {
          self._drawerWarming = false;
        });
    },

    /*========================================================================
      Order note
    ========================================================================*/

    toggleNote: function() {
      if (!this.note) {
        return;
      }

      var willOpen = Boolean(this.note.hidden);
      setHidden(this.note, !willOpen);

      if (this.noteToggle && this.noteToggle.setAttribute) {
        this.noteToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }

      if (willOpen) {
        var field = this.note.querySelector(SELECTORS.noteInput);
        if (field && typeof field.focus === 'function') {
          field.focus();
        }
        // A prefilled note (cart.note survives sessions) should open at its
        // grown height, not clipped to the 3-line minimum.
        this.autogrowNote(field);
      }
    },

    // Autogrow between ~3 lines (the CSS min-height) and 6 lines, then
    // scroll. Height math is content+padding (scrollHeight) plus the 2px of
    // borders box-sizing:border-box folds into style.height.
    autogrowNote: function(field) {
      if (!field || !field.style || typeof field.scrollHeight !== 'number') {
        return;
      }
      var line = 19;
      try {
        var parsed = parseFloat(window.getComputedStyle(field).lineHeight);
        if (parsed > 0) {
          line = parsed;
        }
      } catch (e) { /* keep the fallback */ }
      var max = Math.round(line * 6 + 18 + 2);
      field.style.height = 'auto';
      var grown = Math.min(field.scrollHeight + 2, max);
      field.style.height = grown + 'px';
      field.style.overflowY = field.scrollHeight + 2 > max ? 'auto' : 'hidden';
    },

    getCurrentNote: function() {
      var noteInput = this.root.querySelector(SELECTORS.noteInput);
      return noteInput ? (noteInput.value || '') : '';
    },

    // CartForm's own save is debounced 500ms; closing inside that window would
    // drop the edit, so flush here. Unlike stock (which wrote on EVERY close,
    // dirty or not) this only writes when the textarea actually changed.
    // Returns the write, so a caller that must READ the cart afterwards can
    // wait for it; close() and the page's blur hook do not care and ignore it.
    flushNote: function() {
      var noteInput = this.root.querySelector(SELECTORS.noteInput);
      if (!noteInput || !this.noteDirty || isChallengeActive()) {
        return null;
      }

      this.noteDirty = false;
      return Promise.resolve(theme.cart.updateNote(noteInput.value || '')).catch(function() {});
    },

    /*
      Port of theme.HeaderCart.flushDirtyQuantityInputs: a stepper the shopper
      typed into but never blurred still owes the cart a write.

      Line facts come through findLine() rather than straight off the drawer's
      CartForm, which is what lets the PAGE flush too — companion mode has no
      CartForm of its own, so it answers from the state the event bus carried
      (seeded at first paint). No state for a line means we cannot tell a real
      edit from a repaint, and skipping is the safe half of that.
    */
    flushDirtyQuantityInputs: function() {
      var inputs = this.root.querySelectorAll(SELECTORS.qtyInput);
      var flushed = false;

      for (var i = 0; i < inputs.length; i += 1) {
        var input = inputs[i];
        var key = input && input.dataset && input.dataset.id;
        if (!key || !input.dataset.userDirty || typeof input.dispatchEvent !== 'function') {
          continue;
        }

        // The comparison exists only to skip a write the cart already agrees
        // with. With no authoritative quantity to compare against — a page that
        // shipped without its seed, or a line no state knows yet — write what
        // the shopper typed rather than drop it; a redundant write is cheap and
        // a lost edit is not.
        var item = this.findLine(key);
        var authoritativeQty = item ? parseInt(item.quantity, 10) : NaN;
        if (!isNaN(authoritativeQty) && String(input.value) === String(authoritativeQty)) {
          delete input.dataset.userDirty;
          continue;
        }

        var changeEvent = null;
        if (typeof Event === 'function') {
          changeEvent = new Event('change', { bubbles: true });
        } else if (document.createEvent) {
          changeEvent = document.createEvent('Event');
          changeEvent.initEvent('change', true, true);
        }

        if (changeEvent) {
          input.dispatchEvent(changeEvent);
          delete input.dataset.userDirty;
          flushed = true;
        }
      }

      return flushed;
    },

    /*========================================================================
      Flush before read

      For anything that has to READ the cart and must not read one edit behind
      it. ib-cart-save.js is the caller that exists: a quantity the shopper just
      typed is still sitting in theme-cart.js's 300ms line-write debounce and
      the note in theme.CartForm's 500ms one, so the cart the server would
      answer with is not the cart on screen. Nothing in the cart layer holds a
      read behind a pending write — reads and writes are not serialised against
      each other — so the wait has to happen here.

      Resolves true when something was flushed and has settled, false when there
      was nothing to flush. Never rejects: a save must not be held hostage by a
      write that fails, and a stale snapshot beats no save at all.
    ========================================================================*/

    flushPendingEdits: function() {
      var pending = [];

      var note = this.flushNote();
      if (note) {
        pending.push(note);
      }
      if (this.flushDirtyQuantityInputs()) {
        pending.push(this.whenCartSettles());
      }

      if (!pending.length) {
        return Promise.resolve(false);
      }

      return Promise.all(pending).then(function() {
        return true;
      }, function() {
        return true;
      });
    },

    // Resolves on the first SETTLED cart:updated. theme-cart.js batches every
    // line write in the debounce window into one request, so one settled
    // payload covers every input the flush touched.
    whenCartSettles: function(ceilingMs) {
      var timeout = typeof ceilingMs === 'number' ? ceilingMs : FLUSH_SETTLE_CEILING_MS;

      return new Promise(function(resolve) {
        var settled = false;
        var timer = null;

        function finish() {
          if (settled) {
            return;
          }
          settled = true;
          if (timer !== null) {
            clearTimeout(timer);
          }
          if (typeof document.removeEventListener === 'function') {
            document.removeEventListener('cart:updated', onUpdated);
          }
          resolve();
        }

        function onUpdated(evt) {
          var detail = evt && evt.detail ? evt.detail : {};
          // Optimistic and count-only payloads are the paint, not the answer.
          if (detail.optimistic || detail.countOnly) {
            return;
          }
          finish();
        }

        document.addEventListener('cart:updated', onUpdated);
        timer = setTimeout(finish, timeout);
      });
    },

    /*========================================================================
      Confirm modals
    ========================================================================*/

    getModal: function(name) {
      return document.querySelector('[data-ib-cart-modal="' + name + '"]');
    },

    getOpenModal: function() {
      var modals = document.querySelectorAll(SELECTORS.modal);
      for (var i = 0; i < modals.length; i += 1) {
        if (!modals[i].hidden) {
          return modals[i];
        }
      }
      return null;
    },

    openModal: function(modal, trigger) {
      if (!modal) {
        return;
      }
      this.lastModalTrigger = trigger || null;
      setHidden(modal, false);

      var focusTarget = modal.querySelector(SELECTORS.modalCancel) || modal.querySelector(SELECTORS.focusable);
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus();
      }
    },

    closeModal: function(modal, skipFocus) {
      if (!modal) {
        return;
      }
      setHidden(modal, true);

      if (modal.getAttribute && modal.getAttribute('data-ib-cart-modal') === 'remove') {
        this.pendingRemove = null;
      }

      if (!skipFocus) {
        this.restoreFocus(this.lastModalTrigger);
      }
      this.lastModalTrigger = null;
    },

    /*
      Authoritative cart state. The drawer's own CartForm keeps it; companion
      mode has no CartForm, so the page falls back to the last state the event
      bus carried — seeded at first paint from the [data-ib-cart-state] stamp so
      a remove confirm can name and price a line before anything has mutated.
    */
    cartState: function() {
      if (this.cartForm && this.cartForm._lastCartState) {
        return this.cartForm._lastCartState;
      }
      return this.seededCart || null;
    },

    findLine: function(key) {
      if (this.cartForm && typeof this.cartForm.findCartStateItem === 'function') {
        var found = this.cartForm.findCartStateItem(key);
        if (found) {
          return found;
        }
      }

      var state = this.cartState();
      var items = state && state.items;
      if (!items || typeof items.length !== 'number') {
        return null;
      }
      for (var i = 0; i < items.length; i += 1) {
        if (String(items[i].key) === String(key)) {
          return items[i];
        }
      }
      return null;
    },

    // Line facts come from the authoritative cart state when there is one, and
    // from the rendered row otherwise.
    describeLine: function(key, row) {
      var item = this.findLine(key);

      var name = '';
      var quantity = null;
      var amount = null;

      if (item) {
        name = item.product_title || item.title || '';
        quantity = parseInt(item.quantity, 10);
        amount = item.final_line_price;
      }

      if (row) {
        if (!name) {
          var nameEl = row.querySelector('.cart__item-title');
          name = nameEl ? String(nameEl.textContent || '').trim() : '';
        }
        if (quantity === null || isNaN(quantity)) {
          var qtyEl = row.querySelector(SELECTORS.qtyInput);
          quantity = qtyEl ? parseInt(qtyEl.value, 10) : null;
        }
      }

      return {
        name: name || themeString('cartRemoveThisItem', 'this item'),
        quantity: isNaN(quantity) ? null : quantity,
        amount: amount
      };
    },

    askRemove: function(removeBtn) {
      var key = removeBtn.dataset ? removeBtn.dataset.key : null;
      if (!key) {
        return;
      }

      var row = removeBtn.closest ? removeBtn.closest(SELECTORS.lineItem) : null;
      var line = this.describeLine(key, row);
      var modal = this.getModal('remove');
      if (!modal) {
        return;
      }

      this.pendingRemove = {
        key: key,
        variantId: removeBtn.dataset ? removeBtn.dataset.variantId : null,
        // Where this line sits now, so the focus repair below can land on
        // whichever row takes its place. Read before the mutation, obviously.
        index: this.rowIndexOf(row)
      };

      textInto(modal.querySelector('[data-ib-remove-name]'), line.name);

      var qtyCopy = '';
      if (line.quantity !== null) {
        qtyCopy = unitsLabel(line.quantity);
      }
      var amountCopy = money(line.amount);
      if (qtyCopy && amountCopy) {
        qtyCopy += ' (' + amountCopy + ')';
      } else if (!qtyCopy) {
        qtyCopy = amountCopy || 'it';
      }
      textInto(modal.querySelector('[data-ib-remove-qty]'), qtyCopy);

      this.openModal(modal, removeBtn);
    },

    confirmRemove: function() {
      var pending = this.pendingRemove;
      var modal = this.getModal('remove');
      this.closeModal(modal, true);

      if (!pending) {
        return;
      }

      var self = this;
      this.removeLineItem(pending.key, pending.variantId).catch(function(error) {
        console.warn('Cart remove failed:', error);
        self.showCartActionError((error && error.userMessage) || themeString('cartErrorRemove', 'Unable to remove item. Please try again.'));
      }).then(function() {
        // Land on the row that took the removed one's place, so a keyboard
        // shopper carries on down the list instead of restarting the page.
        self.repairPageFocus(pending.index);
      });
    },

    // Same order as theme.HeaderCart.removeLineItem: the /cart write first, the
    // product-page quantity mirror only after it resolves.
    removeLineItem: function(lineKey, variantId) {
      if (!lineKey) {
        return Promise.reject(new Error('Missing cart item id'));
      }

      return theme.cart.removeDrawerLine(lineKey).then(function(result) {
        theme.productQuantitySync.syncVariantCartQuantity(variantId, 0);
        return result;
      });
    },

    askClear: function(trigger) {
      var modal = this.getModal('clear');
      if (!modal) {
        return;
      }

      var cartState = this.cartState();
      var lines = cartState && cartState.items ? cartState.items.length : this.countRenderedLines();
      var units = cartState && typeof cartState.item_count === 'number'
        ? cartState.item_count
        : this.countRenderedUnits();
      var total = cartState && typeof cartState.total_price === 'number' ? cartState.total_price : null;

      textInto(
        modal.querySelector('[data-ib-clear-count]'),
        itemsLabel(lines) + ' (' + unitsLabel(units) + ')'
      );
      textInto(modal.querySelector('[data-ib-clear-value]'), money(total) || themeString('cartClearFullValue', 'its full value'));

      this.openModal(modal, trigger);
    },

    confirmClear: function() {
      var modal = this.getModal('clear');
      this.closeModal(modal, true);

      var self = this;
      this.clearCartPreservingNote(this.getCurrentNote()).catch(function(error) {
        console.error('Error clearing cart:', error);
        self.showCartActionError((error && error.userMessage) || themeString('cartErrorClear', 'Unable to clear cart. Please try again.'));
      }).then(function() {
        // Nothing is left to land on, so the heading is the survivor.
        self.repairPageFocus();
      });
    },

    rowIndexOf: function(row) {
      if (!row) {
        return -1;
      }
      var rows = this.root.querySelectorAll(SELECTORS.lineItem);
      return Array.prototype.indexOf.call(rows, row);
    },

    /*
      Focus repair, page surface only. Both confirms close their dialog with
      skipFocus, because the control that opened it is about to be taken away by
      the render. In the DRAWER that is harmless — the sheet's own trap catches
      the resulting body focus on the next Tab and pulls it back in. A page has
      no trap by design (trapTab returns early there), so focus just falls to
      <body> and the next Tab restarts at the top of the document, above the
      header, miles from the cart the shopper was working in.
    */
    repairPageFocus: function(rowIndex) {
      if (!this.isPage) {
        return;
      }

      var rows = this.root.querySelectorAll(SELECTORS.lineItem);
      var target = null;

      if (rows && rows.length) {
        var at = typeof rowIndex === 'number' && rowIndex > 0 ? Math.min(rowIndex, rows.length - 1) : 0;
        target = rows[at].querySelector(SELECTORS.removeBtn) || rows[at].querySelector(SELECTORS.focusable);
      }

      if (!target) {
        target = this.root.querySelector(SELECTORS.pageTitle);
        // A heading is not focusable on its own. -1 makes it a landing spot for
        // this move without putting it in anybody's Tab order.
        if (target && target.setAttribute) {
          target.setAttribute('tabindex', '-1');
        }
      }

      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    },

    // Port of theme.HeaderCart.clearCartPreservingNote — the note is read from
    // the textarea and handed back to the clear so it survives.
    clearCartPreservingNote: function(note) {
      return theme.cart.clearDrawerCart(note || '').then(function(result) {
        theme.productQuantitySync.syncAllCartQuantities(0);
        return result;
      });
    },

    showCartActionError: function(message) {
      this.showError(message || themeString('cartErrorUpdate', 'Unable to update cart. Please try again.'));
    }
  };

  /*
    Two queries rather than one comma-joined selector. The drawer and the page
    are mutually exclusive by URL — layout/theme.liquid renders the drawer
    portal `unless request.page_type == 'cart'` — so this is a precedence order,
    not a race, and writing it out says which surface wins if that gate ever
    slips.
  */
  function boot() {
    if (!hasTheme() || typeof theme.CartForm !== 'function') {
      return;
    }

    var root = document.querySelector(SELECTORS.root);
    var surface = 'drawer';

    if (!root) {
      root = document.querySelector(SELECTORS.pageRoot);
      surface = 'page';
    }

    if (!root) {
      return;
    }

    var formSelector = surface === 'page' ? SELECTORS.pageForm : SELECTORS.form;
    if (!root.querySelector(formSelector)) {
      return;
    }

    window.IBCartDrawer = new IBCartDrawer(root, surface);
  }

  boot();
})();
